(() => {
  if (window.__rmsAzEnhancementsLoaded) return;
  window.__rmsAzEnhancementsLoaded = true;

  const normalizeName = (value) => String(value || '').normalize('NFKC').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
  const stripDiacritics = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const NON_PERSON_ARTISTS = new Set([
    'AC/DC', 'Al Qantarah', 'Black Sabbath', 'Canzoniere del Lazio', 'Deep Purple', 'Dream Theater',
    'Pink Floyd', 'Raiz & Radicanto', 'The Velvet Underground', 'Tinere Harpa', 'Vir Dei', 'Vox Hesperia'
  ].map(normalizeName));

  const SURNAME_OVERRIDES = new Map(Object.entries({
    'al di meola': 'Di Meola, Al',
    'arturo benedetti michelangeli': 'Benedetti Michelangeli, Arturo',
    'francesco de palma': 'De Palma, Francesco',
    'giovanni pierluigi da palestrina': 'Palestrina, Giovanni Pierluigi da',
    'giuseppe de trizio': 'De Trizio, Giuseppe',
    'marcello di leonardo': 'Di Leonardo, Marcello',
    'mauro de federicis': 'De Federicis, Mauro',
    'oliviero de fabritiis': 'De Fabritiis, Oliviero',
    'ralph vaughan williams': 'Vaughan Williams, Ralph',
    'roberto della vecchia': 'Della Vecchia, Roberto',
    'vinícius de moraes': 'Moraes, Vinícius de'
  }));

  const GROUP_TERMS = [
    'orchestra', 'orchester', 'orchestre', 'philharmonic', 'philharmoniker', 'philharmonia',
    'symphony', 'symphoniker', 'sinfonica', 'sinfonie', 'choir', 'chor', 'ensemble', 'quartet',
    'quintet', 'trio', 'duo', 'band', 'coro', 'corale', 'filarmonica', 'filarmonici', 'academy',
    'staatskapelle', 'concertgebouw', 'rundfunk', 'solisti', 'chorale'
  ];

  const isNonPersonArtist = (name) => {
    const n = normalizeName(name);
    return NON_PERSON_ARTISTS.has(n) || n.startsWith('the ') || n.includes('&') || GROUP_TERMS.some((term) => n.includes(term));
  };

  const surnameDisplay = (name, isArtist) => {
    const clean = String(name || '').replace(/\s+/g, ' ').trim();
    if (!clean || (isArtist && isNonPersonArtist(clean))) return clean;
    const override = SURNAME_OVERRIDES.get(normalizeName(clean));
    if (override) return override;
    const parts = clean.split(' ');
    if (parts.length < 2) return clean;
    const surname = parts.pop();
    return `${surname}, ${parts.join(' ')}`;
  };

  const firstLetter = (name) => {
    const match = stripDiacritics(name).toUpperCase().match(/[A-Z]/);
    return match ? match[0] : '#';
  };

  const getStored = (key) => { try { return sessionStorage.getItem(key); } catch (_) { return null; } };
  const setStored = (key, value) => { try { sessionStorage.setItem(key, value); } catch (_) {} };

  const setup = (root, type) => {
    if (!root || root.dataset.rmsSurnameReady === 'true') return;
    root.dataset.rmsSurnameReady = 'true';

    const isArtist = type === 'artists';
    const itemSelector = isArtist ? '.rms-artist-group__item' : '.rms-composer-group__item';
    const nameSelector = isArtist ? '.rms-artist-group__name' : '.rms-composer-group__name';
    const groupSelector = isArtist ? '.rms-artist-group' : '.rms-composer-group';
    const groupsSelector = isArtist ? '.rms-artists-az__groups' : '.rms-composers-az__groups';
    const alphabetSelector = isArtist ? '.rms-artists-az__alphabet' : '.rms-composers-az__alphabet';
    const letterClass = isArtist ? 'rms-artists-az__letter' : 'rms-composers-az__letter';
    const groupClass = isArtist ? 'rms-artist-group' : 'rms-composer-group';
    const headingClass = isArtist ? 'rms-artist-group__letter' : 'rms-composer-group__letter';
    const listClass = isArtist ? 'rms-artist-group__list' : 'rms-composer-group__list';
    const idPrefix = isArtist ? 'rms-artist-' : 'rms-composer-';
    const headingPrefix = isArtist ? 'rms-artist-heading-' : 'rms-composer-heading-';
    const storageKey = `rms-az-filter-${type}`;

    const items = Array.from(root.querySelectorAll(itemSelector));
    const groupsContainer = root.querySelector(groupsSelector);
    const alphabet = root.querySelector(alphabetSelector);
    if (!items.length || !groupsContainer || !alphabet) return;

    items.forEach((item) => {
      const nameElement = item.querySelector(nameSelector);
      const naturalName = (item.dataset.rmsNaturalName || nameElement?.textContent || '').replace(/\s+/g, ' ').trim();
      const displayName = surnameDisplay(naturalName, isArtist);
      item.dataset.rmsNaturalName = naturalName;
      item.dataset.rmsDisplayName = displayName;
      item.dataset.rmsLetter = firstLetter(displayName);
      if (nameElement) nameElement.textContent = displayName;
    });

    items.sort((a, b) => a.dataset.rmsDisplayName.localeCompare(b.dataset.rmsDisplayName, document.documentElement.lang || 'it', { sensitivity: 'base' }));
    groupsContainer.innerHTML = '';

    [...new Set(items.map((item) => item.dataset.rmsLetter).filter((letter) => /^[A-Z]$/.test(letter)))].sort().forEach((letter) => {
      const section = document.createElement('section');
      section.className = groupClass;
      section.id = `${idPrefix}${letter}`;
      section.setAttribute('aria-labelledby', `${headingPrefix}${letter}`);
      const heading = document.createElement('h2');
      heading.className = headingClass;
      heading.id = `${headingPrefix}${letter}`;
      heading.textContent = letter;
      const list = document.createElement('ul');
      list.className = listClass;
      list.setAttribute('role', 'list');
      items.filter((item) => item.dataset.rmsLetter === letter).forEach((item) => list.appendChild(item));
      section.append(heading, list);
      groupsContainer.appendChild(section);
    });

    alphabet.innerHTML = '';
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').forEach((letter) => {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = letterClass;
      link.href = `#${idPrefix}${letter}`;
      link.textContent = letter;
      li.appendChild(link);
      alphabet.appendChild(li);
    });

    const refresh = () => {
      root.querySelectorAll(groupSelector).forEach((group) => {
        group.hidden = !Array.from(group.querySelectorAll(itemSelector)).some((item) => !item.hidden);
      });
      root.querySelectorAll(`a.${letterClass}`).forEach((link) => {
        const href = link.getAttribute('href') || '';
        const target = href.startsWith('#') ? root.querySelector(href) : null;
        const disabled = !target || target.hidden;
        link.classList.toggle('is-disabled', disabled);
        link.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        link.tabIndex = disabled ? -1 : 0;
      });
    };

    const controls = root.querySelector('.rms-az-filters');
    if (controls) {
      controls.addEventListener('click', (event) => {
        const button = event.target.closest('.rms-az-filter');
        if (!button) return;
        setStored(storageKey, button.dataset.rmsFilter === 'other' ? 'other' : 'classical');
        requestAnimationFrame(refresh);
      });

      const stored = getStored(storageKey);
      if (stored === 'other' || stored === 'classical') {
        const button = controls.querySelector(`[data-rms-filter="${stored}"]`);
        if (button && button.getAttribute('aria-pressed') !== 'true') button.click();
      }
    }

    root.addEventListener('click', (event) => {
      const link = event.target.closest(`a.${letterClass}`);
      if (link?.getAttribute('aria-disabled') === 'true') event.preventDefault();
    });

    refresh();
  };

  const init = () => {
    setup(document.querySelector('.rms-artists-az'), 'artists');
    setup(document.querySelector('.rms-composers-az'), 'composers');
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
